import { Icon, IconButton, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiMoreHorizontal, FiSave, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  SourcingEventStatus,
  useDeleteSourcingEventMutation,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { isOwnerOrAdmin } from "../../../../../common/auth/utils";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../common/errors/useApiError";
import { ALL_SECTIONS, buildTemplateContent } from "../../../../../common/library/templateFromEvent";
import { sectionsDefinedBy } from "../../../../../common/library/templateSelection";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { urls } from "../../../../../urls";

interface Props {
  eventId: string;
}

export const WizardActionsMenu = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [deleteEvent] = useDeleteSourcingEventMutation();
  const { state, flushPending } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { selectedOrg } = useLoggedInWithOrgContextState();

  const confirmDiscard = () => {
    confirmationModal.show({
      title: t("Discard the draft?"),
      description: t("The draft and everything registered on it is permanently deleted."),
      confirmButtonColor: "red",
      confirmButtonText: t("Discard") ?? "",
      onConfirmed: async () => {
        try {
          await deleteEvent({ eventId }).unwrap();
          navigate(urls.v2.sourcingEvents.viewAll);
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const saveAsTemplate = async () => {
    try {
      await flushPending();
    } catch (e) {
      trigger(e);
      return;
    }
    navigate(urls.v2.maler.new.go({ fromEvent: eventId }));
  };

  const canDiscard = state.status === SourcingEventStatus.Draft;
  const canSaveAsTemplate = isOwnerOrAdmin(selectedOrg.roles);
  const hasNothingToTemplate =
    sectionsDefinedBy(buildTemplateContent(state.eventData, ALL_SECTIONS)).length === 0;

  if (!canDiscard && !canSaveAsTemplate) return null;

  return (
    <>
      {confirmationModal.modal}
      <Menu>
        <MenuButton
          as={IconButton}
          size="sm"
          variant="outline"
          aria-label={t("Actions") ?? "Actions"}
          icon={<Icon as={FiMoreHorizontal} />}
        />
        <MenuList>
          {canSaveAsTemplate && (
            <MenuItem
              icon={<Icon as={FiSave} />}
              isDisabled={hasNothingToTemplate}
              onClick={() => void saveAsTemplate()}
            >
              {t("Save as template")}
            </MenuItem>
          )}
          {canDiscard && (
            <MenuItem color="smError" icon={<Icon as={FiTrash2} />} onClick={confirmDiscard}>
              {t("Discard the draft")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};
